import React, { useEffect, useRef, useState } from 'react'
import ConstantConsumptionStyle from './ConstantConsumption'
import Button from '../../../components/Button'
import { useNavigate } from 'react-router-dom'
import api, { customFuncsApi } from '../../../api'
import Layout from '../../../components/Layout'
import ClipLoader from 'react-spinners/ClipLoader'
import { get, set } from 'lodash'
import { useSelector } from 'react-redux'
import { ErrorModal, SuccessModal } from '../../../components/Modal'
import { formatNumberWithIntlV2 } from '../../../utils/formatNumber'
import { removeSpaces } from '../../../utils/removeSpaces'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { AutoComplete } from 'antd'

const CreateJournalEntries = () => {
	const { getMe } = useSelector((state) => state.main)
	const navigation = useNavigate()
	const { t } = useTranslation()
	const inputRef = useRef(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isAccountsLoading, setIsAccountsLoading] = useState({
		chartAccounts: false,
		yesterdaysAccounts: false,
	})
	const [date, setDate] = useState(new Date().toISOString().split('T')[0])
	const errorRef = useRef()
	const successModalRef = useRef()
	const [currency, setCurrency] = useState('USD')
	const [cashAccountsData, setCashAccountsData] = useState([])
	const [formData, setFormData] = useState({
		ToCash: 5010, // На сейф
		ToCashBalance: 0,
		FromCash: '', // С кассы,
		FromCashBalance: 0,
		CashSum: '',
		AccountCode: '',
		FromCashLabel: '',
		ToCashLabel: '',
	})
	const [yesterdayBalance, setYesterdayBalance] = useState([])
	const [btnLoading, setBtnLoading] = useState(false)
	const [remarks, setRemarks] = useState(null)
	const [cashCodes, setCashCodes] = useState('5030,5020,5040,5722')

	const handleSpendAccountClick = (event) => {
		if (inputRef.current) {
			inputRef.current.value = event.target.innerText
		}
		const code = event.target.value
		setFormData((prev) => ({
			...prev,
			ToCash: code,
		}))
	}

	const cashAccounts = async () => {
		setIsAccountsLoading((prev) => ({
			...prev,
			chartAccounts: true,
		}))
		const data = await api(
			`ChartOfAccounts?$select=Code,Name,AcctCurrency,Balance,Balance_FrgnCurr&$filter=startswith(Code, '5') and ExternalCode eq '1' and Code ne '5011' and Code ne '5050' and Code ne '5051' and (AcctCurrency eq '${currency}' or AcctCurrency eq '%23%23')&$orderby=Code asc`,
			{
				headers: {
					Prefer: 'odata.maxpagesize=10000',
				},
			},
		).then((res) => {
			const data = JSON.parse(res.data).value
			const cashCodes = data.map((item) => item.Code).join(',')
			setCashCodes(cashCodes)
			return data
		})
		setCashAccountsData(data)
		setFormData((prev) => ({
			...prev,
			FromCash: data[0]?.Code,
			FromCashBalance:
				currency === 'UZS' ? data[0].Balance_FrgnCurr : data[0]?.Balance,
		}))
		setIsAccountsLoading((prev) => ({
			...prev,
			chartAccounts: false,
		}))
	}

	const getYesterdaysBalance = async () => {
		// const yesterday = moment(date).subtract(1, 'day').format('YYYY-MM-DD')

		setIsAccountsLoading((prev) => ({
			...prev,
			yesterdaysAccounts: true,
		}))
		await customFuncsApi
			.get(`xsSql/getAccountsBalance?accounts=${cashCodes}&dateFilter=${date}`)
			.then((res) => {
				const data = res.data.value || []
				setYesterdayBalance(data)
			})
			.catch((err) => {
				errorRef.current?.open(
					get(JSON.parse(err.response.data), 'error.message', ''),
				)
			})
			.finally(() => {
				setIsAccountsLoading((prev) => ({
					...prev,
					yesterdaysAccounts: false,
				}))
			})
	}

	useEffect(() => {
		getYesterdaysBalance()
	}, [currency, cashCodes, date])

	useEffect(() => {
		cashAccounts()
	}, [currency])

	const submitForm = () => {
		handleConfirm()
	}

	const handleConfirm = async () => {
		setBtnLoading(true)
		try {
			const response = await api.post('JournalEntries', {
				ReferenceDate: date + 'T00:00:00Z',
				U_NumDoc: 'Send Money',
				Memo: remarks ? remarks : null,
				U_Creator: getMe?.EmployeeID,
				JournalEntryLines: [
					{
						AccountCode: formData.ToCash,
						Debit: currency === 'USD' ? removeSpaces(formData.CashSum) : 0,
						Credit: 0,
						FCDebit: currency === 'UZS' ? removeSpaces(formData.CashSum) : 0,
						FCCredit: 0,
						FCCurrency: currency === 'UZS' ? currency : '',
						ContraAccount: formData.FromCash,
					},
					{
						AccountCode: formData.FromCash,
						Debit: 0,
						Credit: currency === 'USD' ? removeSpaces(formData.CashSum) : 0,
						FCDebit: 0,
						FCCredit: currency === 'UZS' ? removeSpaces(formData.CashSum) : 0,
						FCCurrency: currency === 'UZS' ? currency : '',
						ContraAccount: formData.ToCash,
					},
				],
			})
			successModalRef.current?.open('Бугалтерский операция успешно создана!')
		} catch (err) {
			errorRef.current?.open(
				get(JSON.parse(err.response.data), 'error.message', ''),
			)
		} finally {
			setBtnLoading(false)
		}
	}

	const handleDateChange = (event) => {
		setDate(event.target.value)
	}

	const handleCurrencyChange = (event) => {
		setCurrency(event.target.value)
		setCashAccountsData([])
		if (inputRef.current) {
			inputRef.current.value = ''
		}
		setFormData((prev) => ({
			...prev,
			CashSum: '',
			ToCash: event.target.value === 'UZS' ? 5011 : 5010,
		}))
	}

	const handleClickAccountCode = (event) => {
		const cashAccount = cashAccountsData.find(
			(account) => account.Code === event.target.value,
		)

		setFormData((prev) => ({
			...prev,
			FromCash: cashAccount.Code,
			FromCashBalance:
				currency === 'UZS'
					? cashAccount.Balance_FrgnCurr
					: cashAccount?.Balance,
		}))
	}

	const handleRemarks = (event) => {
		setRemarks(event.target.value)
	}

	return (
		<Layout>
			<ConstantConsumptionStyle>
				<div className="container">
					<p className="productTitle text-xl font-bold text-center py-8">
						{t('addJournalEntries')}
					</p>

					{isLoading ? (
						<ClipLoader
							loading={true}
							size={75}
							aria-label="Loading Spinner"
							className={'loader'}
							data-testid="loader"
						/>
					) : (
						<div>
							<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
								<div className="flex flex-col">
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('currency')}
									</p>
									<select
										className={
											'border-[1px] border-[#DFE2E9"] p-1 rounded-md w-full flex-1'
										}
										onChange={handleCurrencyChange}
									>
										<option value="USD">USD</option>
										<option value="UZS">UZS</option>
									</select>
								</div>
								<div className="flex flex-col">
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('toSeyf')}
									</p>
									<AutoComplete
										value={formData.ToCashLabel || undefined}
										className="w-full"
										// options={
										// 	currency === 'UZS'
										// 		? [{ value: 5011, label: "Asosiy naqd so'm - 5011" }]
										// 		: [{ value: 5010, label: 'Asosiy naqd $ - 5010' }]
										// }
										options={cashAccountsData
											.filter(
												(item) =>
													item.Code !== '5050' &&
													item.Code !== '5051' &&
													item.Code !== '5011',
											)
											.map((item) => ({
												value: item.Code,
												label: `${item.Name} - ${item.Code}`,
											}))}
										placeholder={t('toSeyf')}
										onSelect={(value, option) => {
											setFormData((prev) => ({
												...prev,
												ToCash: value, // backend value
												ToCashLabel: option.label, // UI label
											}))
										}}
										onChange={(val) => {
											if (!val) {
												// input cleared
												setFormData((prev) => ({
													...prev,
													ToCash: '',
													ToCashLabel: '',
												}))
											} else {
												// typing without selecting
												setFormData((prev) => ({
													...prev,
													ToCashLabel: val,
												}))
											}
										}}
										filterOption={(input, option) =>
											option?.value
												?.toString()
												.toLowerCase()
												.includes(input.toLowerCase()) ||
											option?.label
												?.toString()
												.toLowerCase()
												.includes(input.toLowerCase())
										}
									/>
								</div>
								<div className="flex flex-col">
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('date')}
									</p>
									<input
										className={
											'border-[1px] border-[#DFE2E9"] p-1 rounded-md w-full flex-1'
										}
										type="date"
										defaultValue={date}
										onChange={handleDateChange}
									/>
								</div>
							</div>
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:grid-cols-3">
								<div>
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('fromCash')}
									</p>
									<AutoComplete
										value={formData.FromCashLabel || undefined}
										className="w-full"
										disabled={get(getMe, 'Department2.Name', '') === 'Sotuv'}
										options={cashAccountsData
											.filter(
												(item) =>
													item.Code !== '5050' &&
													item.Code !== '5051' &&
													item.Code !== '5011',
											)
											.map((item) => ({
												value: item.Code,
												label: `${item.Name} - ${item.Code}`,
											}))}
										placeholder={t('fromCash')}
										onSelect={(value, option) => {
											setFormData((prev) => ({
												...prev,
												FromCash: value,
												FromCashLabel: option.label,
											}))
										}}
										onChange={(val) => {
											if (!val) {
												setFormData((prev) => ({
													...prev,
													FromCash: '',
													FromCashLabel: '',
												}))
											} else {
												setFormData((prev) => ({
													...prev,
													FromCashLabel: val,
												}))
											}
										}}
										filterOption={(input, option) =>
											option?.value
												?.toString()
												.toLowerCase()
												.includes(input.toLowerCase()) ||
											option?.label
												?.toString()
												.toLowerCase()
												.includes(input.toLowerCase())
										}
									/>

									{isAccountsLoading.chartAccounts ||
									isAccountsLoading.yesterdaysAccounts ? (
										<p className="text-zinc-600 mt-3">Загрузка...</p>
									) : (
										<>
											{/* <p className="text-zinc-600 mt-3">
												{t('accBalance')}:{' '}
												<span className="font-bold">
													{' '}
													{formatNumberWithIntlV2(
														formData.FromCashBalance,
													)}{' '}
													{currency}
												</span>
											</p> */}
											<p className="text-zinc-600 mt-3">
												{t('accBalance')}:
												<span className="font-bold">
													{' '}
													{formatNumberWithIntlV2(
														yesterdayBalance.find(
															(item) => item.Code === formData.FromCash,
														)?.Balance || 0,
													)}{' '}
													{currency}
												</span>
											</p>
										</>
									)}
								</div>
								<div>
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('price')}
									</p>
									<input
										className={
											'border-[1px] border-[#DFE2E9"] p-1 rounded-md w-full flex-1'
										}
										type="text"
										onChange={(e) =>
											setFormData((prev) => ({
												...prev,
												CashSum: removeSpaces(e.target.value),
											}))
										}
										onWheel={(e) => e.target.blur()}
										value={formatNumberWithIntlV2(formData.CashSum)}
									/>
								</div>
								<div>
									<p className={'font-medium text-zinc-600 text-sm mb-1'}>
										{t('comment')}
									</p>
									<textarea
										className={
											'border-[1px] border-[#DFE2E9"] p-1 rounded-md w-full flex-1'
										}
										onChange={handleRemarks}
									/>
								</div>
							</div>
							<div className="flex justify-center py-4">
								<Button
									onClick={submitForm}
									isLoading={btnLoading}
									disabled={
										!formData.FromCash ||
										!formData.ToCash ||
										!(+formData.CashSum > 0)
									}
								>
									Добавить
								</Button>
							</div>
						</div>
					)}
				</div>
			</ConstantConsumptionStyle>

			<ErrorModal
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
			<SuccessModal
				getRef={(r) => {
					successModalRef.current = r
				}}
				onConfirm={() => {
					navigation('/journal-entries')
				}}
			/>
		</Layout>
	)
}

export default CreateJournalEntries
